import * as React from 'react'
import { CreateInvoiceDetail } from '../InvoiceDetailStore'
import { DuplicateButton } from './DuplicateButton'
import { IInvoice, TaxGroup, IInvoiceLine } from '@getgreenline/homi-shared'
import { InventoryLogAPI } from '@getgreenline/inventory'
import { IInventoryLog } from '@getgreenline/inventory/build/models/inventoryLog'
import { SaveInvoiceDetailButton } from './SaveInvoiceDetailButton'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { parseErrorMsg, parseAndDownloadCSV } from '../../../../../utilities/helpers'
import { message, Drawer, Menu, Dropdown, Button, Icon } from 'antd'
import { InventoryLog } from '../Main/InventoryLog'
import { inject, observer } from 'mobx-react'
import { Routes } from '../../../../../constants/Routes'
import { ImportPOModal } from '../Main/Receipt/ImportPOModal'

interface Props {
  createInvoice: CreateInvoiceDetail
  widthBreakPoint?: number
  getInvoiceTaxGroup: (invoiceDetail: IInvoice) => TaxGroup | undefined
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isLogDrawerOpen: boolean
  isModalVisible: boolean
  isDownloading: boolean
  inventoryLogs: IInventoryLog[]
}

@inject('currentCompanyStore')
@observer
export class InvoiceDetailHeader extends React.Component<Props & InjectedProps, State> {
  company = this.props.currentCompanyStore!.company!
  createInvoice = this.props.createInvoice

  constructor(props: Props & InjectedProps) {
    super(props)

    this.state = {
      isLogDrawerOpen: false,
      isModalVisible: false,
      isDownloading: false,
      inventoryLogs: [],
    }
  }

  setIsLogDrawerOpen = (isLogDrawerOpen: boolean) => this.setState({ isLogDrawerOpen })

  setIsModalVisible = (isModalVisible: boolean) => this.setState({ isModalVisible })

  setIsDownloading = (isDownloading: boolean) => this.setState({ isDownloading })

  setInventoryLogs = (inventoryLogs: IInventoryLog[]) => this.setState({ inventoryLogs })

  showLogDrawerOpen = async (invoiceId: string) => {
    this.setIsLogDrawerOpen(true)

    try {
      const { data } = await InventoryLogAPI.getInventoryLogs(this.company.id, {
        limit: 200,
        offset: 0,
        invoiceId,
      })

      this.setInventoryLogs(data)
    } catch (error) {
      const errorMessage =
        parseErrorMsg(error) || 'An error occurred while trying to get inventory logs'
      message.error(errorMessage)
    }
  }

  printBarcode = (lines: IInvoiceLine[]) => {
    const locations = this.props.currentCompanyStore?.locations || []
    const selectedLocation = locations.find(
      (location) => location.id === this.createInvoice.locationId,
    )
    const configString = JSON.stringify({
      products: lines.map((product) => {
        return {
          productId: product.productId,
          quantity: product.productUnit === 'unit' ? product.quantity || 1 : 1,
        }
      }),
      locationId: selectedLocation?.id,
    })

    window.localStorage.setItem('printing-config', configString)
    window.open(Routes.PRINTING_CONFIG_VIEW(this.company.id, null), '_blank')
  }

  exportAsCsv = (invoiceDetail: IInvoice) => {
    const csvTitle = `purchase-order-${invoiceDetail.customId}-${invoiceDetail.id}`

    this.setIsDownloading(true)

    parseAndDownloadCSV(csvTitle, this.props.createInvoice.invoiceLinesInCsv)

    this.setIsDownloading(false)
  }

  renderExportMenu = (invoiceDetail: IInvoice): JSX.Element => {
    return (
      <Menu>
        <Menu.Item onClick={() => this.exportAsCsv(invoiceDetail)}>Export as CSV</Menu.Item>
        <Menu.Item>
          <a
            href={Routes.PRINT_INVOICE(this.company.id, invoiceDetail.id)}
            rel='noopener noreferrer'
            target='_blank'
          >
            Export as PDF
          </a>
        </Menu.Item>
      </Menu>
    )
  }

  get saveButtonTitle() {
    const { isSaved, isEdited } = this.props.createInvoice

    return (
      <span>
        <span>Save</span>
        {isEdited && !isSaved && (
          <span>
            <Icon className='ml-1 mr-1' type='issues-close' />
            <small>unsaved changes</small>
          </span>
        )}
      </span>
    )
  }

  render() {
    const { createInvoice, widthBreakPoint, getInvoiceTaxGroup } = this.props
    const { isEditable, isEditMode, invoiceDetail } = createInvoice
    const { isLogDrawerOpen, isModalVisible, inventoryLogs } = this.state

    return (
      <span className='spaced-button-wrapper'>
        {invoiceDetail && (
          <>
            {!isEditable && (
              <BlazeButton
                buttonProps={{
                  onClick: () => this.showLogDrawerOpen(invoiceDetail.id),
                  icon: 'unordered-list',
                }}
                titleProps={{
                  title: 'Inventory logs',
                  widthBreakPoint,
                }}
              />
            )}

            <BlazeButton
              buttonProps={{
                onClick: () => this.printBarcode(invoiceDetail.lines),
                icon: 'barcode',
              }}
              titleProps={{
                title: 'Print barcodes',
                widthBreakPoint,
              }}
            />

            <DuplicateButton
              title='Duplicate'
              widthBreakPoint={widthBreakPoint}
              createInvoice={createInvoice}
              getInvoiceTaxGroup={getInvoiceTaxGroup}
            />
          </>
        )}

        {isEditable && (
          <BlazeButton
            buttonProps={{
              onClick: () => this.setIsModalVisible(true),
              icon: 'upload',
            }}
            titleProps={{
              title: 'Import',
              widthBreakPoint,
            }}
          />
        )}

        {invoiceDetail && (
          <Dropdown overlay={this.renderExportMenu(invoiceDetail)}>
            <Button>
              <Icon type='download' />
              <span>Export</span>
            </Button>
          </Dropdown>
        )}

        {isEditMode && (
          <SaveInvoiceDetailButton
            title={this.saveButtonTitle}
            widthBreakPoint={widthBreakPoint}
            createInvoice={createInvoice}
            getInvoiceTaxGroup={getInvoiceTaxGroup}
          />
        )}

        {isLogDrawerOpen && (
          <Drawer
            title='Inventory logs'
            maskClosable
            placement='right'
            closable={false}
            width={400}
            onClose={() => this.setIsLogDrawerOpen(false)}
            visible={isLogDrawerOpen}
          >
            <InventoryLog inventoryLogs={inventoryLogs} />
          </Drawer>
        )}

        {isModalVisible && (
          <ImportPOModal
            createInvoice={createInvoice}
            onHide={() => this.setIsModalVisible(false)}
          />
        )}
      </span>
    )
  }
}
